import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Divider,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Typography,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ClientAutocomplete from '../common/ClientAutocomplete';
import { validateEmail, validateMobile, validateRequired } from '../../utils/validationUtils';
import { getTopLevelCategories, navigateHierarchy, getLeafProductFromPath, getPathToLeafSlug } from '../../utils/productHierarchyUtils';
import { MEDIUM_OPTIONS, SOURCE_MAPPING } from '../../config/mediumSourceConfig';
import { ENQUIRY_STATUS } from '../../config/statusConfig';
import StatusChip from '../common/StatusChip';
import VirtualizedAutocomplete from '../common/VirtualizedAutocomplete';
import enquiriesApi from '../../services/enquiriesApi';
import countryData from '../../data/country.json';
import stateCityData from '../../data/state_city.json';

/**
 * Add Enquiry Dialog Component
 * Handles both CREATE and EDIT modes. When enquiryId is set, opens in edit mode with pre-filled data.
 */
const AddEnquiryDialog = ({
  open,
  onClose,
  onSubmit,
  enquiryId = null, // When set, dialog is in EDIT mode; otherwise CREATE
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEditMode = Boolean(enquiryId);

  // Component state
  const [loading, setLoading] = useState(false);

  // Product hierarchy state
  const [productSelectionPath, setProductSelectionPath] = useState([]); // Array of selected slugs representing the path

  // Components / requirements state (array of component cards)
  const defaultRequirementItem = () => ({
    item_slug: '',
    item_name: '',
    item_unit_price: 0,
    item_quantity: 1,
    item_total_price: 0,
    included_items: [],
    additional_details: '',
  });
  const [requirement, setRequirement] = useState([]);

  // Form state (client_id kept for EDIT mode so backend preserves client link)
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_email: '',
    client_mobile: '',
    city: '',
    state: '',
    country: '',
    country_code: '',
    enquiry_date: dayjs().format('YYYY-MM-DD'),
    main_product_type: '',
    main_product_type_slug: '',
    medium: '',
    source: '',
    source_comment: '',
    status: '', // Enquiry status
    comment: ''
  });

  // UI state
  const [isNewClient, setIsNewClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [errors, setErrors] = useState({});

  // Ref to ignore stale fetch responses (e.g. user closes dialog or opens another edit before fetch completes)
  const loadingEnquiryIdRef = useRef(null);

  /** Reset form to initial state (CREATE mode). Used on close and when opening for create. */
  const resetForm = useCallback(() => {
    setFormData({
      client_id: '',
      client_name: '',
      client_email: '',
      client_mobile: '',
      city: '',
      state: '',
      country: '',
      country_code: '',
      enquiry_date: dayjs().format('YYYY-MM-DD'),
      main_product_type: '',
      main_product_type_slug: '',
      medium: '',
      source: '',
      source_comment: '',
      status: '',
      comment: ''
    });
    setIsNewClient(false);
    setSelectedClient(null);
    setProductSelectionPath([]);
    setRequirement([]);
    setErrors({});
  }, []);

  /**
   * Hydrate form from an existing enquiry (EDIT mode).
   * Maps every enquiry field explicitly to form state. Single source of truth; all inputs controlled.
   */
  const hydrateForm = useCallback((enquiry) => {
    if (!enquiry) return;

    // Enquiry date: normalize to YYYY-MM-DD for DatePicker
    const rawDate = enquiry.enquiry_date || enquiry.initialEnquiryDate;
    let enquiryDate = dayjs().format('YYYY-MM-DD');
    if (rawDate) {
      if (typeof rawDate === 'string' && rawDate.includes('T')) {
        enquiryDate = rawDate.split('T')[0];
      } else {
        const d = dayjs(rawDate);
        enquiryDate = d.isValid() ? d.format('YYYY-MM-DD') : enquiryDate;
      }
    }

    // Form data: explicit mapping for every field (no implicit spread)
    setFormData({
      client_id: enquiry.client_id != null ? String(enquiry.client_id) : '',
      client_name: enquiry.clientName ?? enquiry.client_name ?? '',
      client_email: enquiry.client_email ?? '',
      client_mobile: enquiry.client_mobile ?? '',
      city: enquiry.city ?? '',
      state: enquiry.state ?? '',
      country: enquiry.country ?? '',
      country_code: enquiry.country_code ?? '',
      enquiry_date: enquiryDate,
      main_product_type: enquiry.main_product_type ?? enquiry.mainProductType ?? '',
      main_product_type_slug: enquiry.main_product_type_slug ?? '',
      medium: enquiry.medium ?? '',
      source: enquiry.source ?? '',
      source_comment: (enquiry.source_comment ?? '').trim(),
      status: enquiry.status ?? enquiry.current_status ?? '',
      comment: (enquiry.comment ?? '').trim(),
    });

    // Client autocomplete: selected value must match option shape { _id, client_name }
    const clientId = enquiry.client_id != null ? String(enquiry.client_id) : '';
    const clientName = enquiry.clientName ?? enquiry.client_name ?? '';
    setSelectedClient(
      clientId || clientName
        ? { _id: clientId, client_name: clientName }
        : null
    );
    setIsNewClient(false);

    // Product selection: path of slugs from root to leaf for dropdowns
    const slug = enquiry.main_product_type_slug ?? '';
    setProductSelectionPath(getPathToLeafSlug(slug));

    // Requirements: full hydrate with correct totals (item_total_price = unit * quantity)
    const reqList = enquiry.requirement ?? enquiry.requirements ?? [];
    const hydratedRequirements =
      reqList.length > 0
        ? reqList.map((r) => {
            const unitPrice = typeof r.item_unit_price === 'number' ? r.item_unit_price : parseFloat(r.item_unit_price) || 0;
            const qty = Math.max(1, parseInt(r.item_quantity, 10) || 1);
            return {
              item_slug: r.item_slug ?? '',
              item_name: r.item_name ?? '',
              item_unit_price: unitPrice,
              item_quantity: qty,
              item_total_price: unitPrice * qty,
              included_items: r.included_items ?? [],
              additional_details: (r.additional_details ?? '').trim(),
            };
          })
        : [];
    setRequirement(hydratedRequirements);
    setErrors({});
  }, []);

  // Edit-mode init: set enquiryId BEFORE opening so dialog opens with correct mode. Clear on close.
  // When dialog opens: load enquiry for edit (only hydrate when fetch matches current enquiryId) or reset for create.
  useEffect(() => {
    if (!open) {
      loadingEnquiryIdRef.current = null;
      return;
    }
    if (enquiryId) {
      const currentId = enquiryId;
      loadingEnquiryIdRef.current = currentId;
      setErrors((prev) => (prev.form ? { ...prev, form: undefined } : prev));
      setLoading(true);
      enquiriesApi
        .get(currentId)
        .then((enquiry) => {
          // Only hydrate if this response is still for the enquiry we're editing (avoid stale overwrite)
          if (loadingEnquiryIdRef.current !== currentId) return;
          hydrateForm(enquiry);
        })
        .catch(() => {
          if (loadingEnquiryIdRef.current !== currentId) return;
          setErrors({ form: 'Failed to load enquiry details' });
        })
        .finally(() => {
          if (loadingEnquiryIdRef.current === currentId) {
            setLoading(false);
          }
        });
    } else {
      loadingEnquiryIdRef.current = null;
      resetForm();
    }
  }, [open, enquiryId, hydrateForm, resetForm]);

  /** Generate item_slug from item_name: lowercase, spaces to _, remove special chars */
  const slugFromItemName = useCallback((name) => {
    if (!name || typeof name !== 'string') return '';
    return name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }, []);

  const addComponentCard = () => {
    setRequirement((prev) => [...prev, defaultRequirementItem()]);
  };

  const removeComponentCard = (index) => {
    setRequirement((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`requirement_${index}_item_name`];
      return next;
    });
  };

  const updateRequirementItem = (index, field, value) => {
    setRequirement((prev) => {
      const next = [...prev];
      const item = { ...next[index], [field]: value };
      if (field === 'item_name') {
        item.item_slug = slugFromItemName(value);
      }
      if (field === 'item_unit_price') {
        const unitPrice = parseFloat(value) || 0;
        item.item_unit_price = unitPrice;
        item.item_quantity = Math.max(1, parseInt(item.item_quantity, 10) || 1);
        item.item_total_price = unitPrice * item.item_quantity;
      }
      if (field === 'item_quantity') {
        const qty = Math.max(1, parseInt(value, 10) || 1);
        item.item_quantity = qty;
        item.item_unit_price = parseFloat(item.item_unit_price) || 0;
        item.item_total_price = item.item_unit_price * qty;
      }
      next[index] = item;
      return next;
    });
    if (field === 'item_name' && errors[`requirement_${index}_item_name`]) {
      setErrors((prev) => ({ ...prev, [`requirement_${index}_item_name`]: '' }));
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => {
      const next = { ...prev, [fieldId]: value };
      // Country change: reset state and city
      if (fieldId === 'country') {
        next.state = '';
        next.city = '';
      }
      // State change: reset city
      if (fieldId === 'state') {
        next.city = '';
      }
      if (fieldId === 'medium' && !value) next.source = '';
      return next;
    });
    if (errors[fieldId]) setErrors(prev => ({ ...prev, [fieldId]: '' }));
  };

  // Location dropdown options (virtualized). Country → State → City hierarchy; state/city from India data.
  const countryOptions = useMemo(() => (Array.isArray(countryData) ? countryData.map((c) => c.name) : []), []);
  const stateOptions = useMemo(() => {
    if (formData.country !== 'India') return [];
    return Array.isArray(stateCityData?.states) ? stateCityData.states.map((s) => s.name) : [];
  }, [formData.country]);
  const cityOptions = useMemo(() => {
    if (!formData.state) return [];
    const stateObj = (stateCityData?.states || []).find((s) => s.name === formData.state);
    if (!stateObj) return [];
    const cities = stateCityData?.city?.[String(stateObj.id)];
    return Array.isArray(cities) ? cities.map((c) => c.name) : [];
  }, [formData.state]);

  const handleClientSelect = (client) => {
    if (!client) {
      setSelectedClient(null);
      handleFieldChange('client_name', '');
      handleFieldChange('client_id', '');
      handleFieldChange('client_email', '');
      handleFieldChange('client_mobile', '');
      handleFieldChange('city', '');
      handleFieldChange('state', '');
      handleFieldChange('country', '');
      handleFieldChange('country_code', '');
      return;
    }

    setSelectedClient(client);
    setIsNewClient(false);
    setFormData(prev => ({
      ...prev,
      client_id: client._id || '',
      client_name: client.client_name,
      client_email: client.client_email || '',
      client_mobile: client.client_mobile || '',
      city: client.city || '',
      state: client.state || '',
      country: client.country || '',
      country_code: client.country_code || ''
    }));

    // Clear any client-related errors
    const clientFields = ['client_name', 'client_email', 'client_mobile', 'city', 'state', 'country'];
    setErrors(prev => {
      const newErrors = { ...prev };
      clientFields.forEach(field => delete newErrors[field]);
      return newErrors;
    });
  };

  const handleNewClientClick = () => {
    setIsNewClient(true);
    setSelectedClient(null);
    setFormData(prev => ({
      ...prev,
      client_id: '',
      client_email: '',
      client_mobile: '',
      city: '',
      state: '',
      country: '',
      country_code: ''
    }));

    // Clear client-related errors
    const clientFields = ['client_name', 'client_email', 'client_mobile'];
    setErrors(prev => {
      const newErrors = { ...prev };
      clientFields.forEach(field => delete newErrors[field]);
      return newErrors;
    });
  };

  const handleBackToClientList = () => {
    setIsNewClient(false);
    setSelectedClient(null);
    handleFieldChange('client_id', '');
    handleFieldChange('client_name', '');
    handleFieldChange('client_email', '');
    handleFieldChange('client_mobile', '');
    handleFieldChange('city', '');
    handleFieldChange('state', '');
    handleFieldChange('country', '');
    handleFieldChange('country_code', '');
  };

  const handleProductCategorySelect = (level, value) => {
    if (!value) {
      // Clear selection from this level onwards
      const newPath = productSelectionPath.slice(0, level);
      setProductSelectionPath(newPath);
      
      // Clear product fields
      setFormData(prev => ({
        ...prev,
        main_product_type: '',
        main_product_type_slug: ''
      }));
      
      // Clear error
      if (errors.main_product_type) {
        setErrors(prev => ({ ...prev, main_product_type: '' }));
      }
      return;
    }

    // Update path up to current level
    const newPath = [...productSelectionPath.slice(0, level), value];
    setProductSelectionPath(newPath);

    // Check if this is a leaf product
    const leafProduct = getLeafProductFromPath(newPath);
    
    if (leafProduct) {
      // Final product selected
      setFormData(prev => ({
        ...prev,
        main_product_type: leafProduct.main_product_type,
        main_product_type_slug: leafProduct.main_product_type_slug
      }));
      
      // Clear error
      if (errors.main_product_type) {
        setErrors(prev => ({ ...prev, main_product_type: '' }));
      }
    } else {
      // Still navigating - clear product fields until leaf is reached
      setFormData(prev => ({
        ...prev,
        main_product_type: '',
        main_product_type_slug: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate client name (always required)
    const clientNameValidation = validateRequired(formData.client_name, 'Client Name');
    if (!clientNameValidation.valid) {
      newErrors.client_name = clientNameValidation.message;
    }

    // Validate email (required for new clients, auto-filled for existing)
    if (isNewClient || !selectedClient) {
      const emailValidation = validateEmail(formData.client_email);
      if (!emailValidation.valid) {
        newErrors.client_email = emailValidation.message;
      }
    }

    // Validate mobile (required for new clients, auto-filled for existing)
    if (isNewClient || !selectedClient) {
      const mobileValidation = validateMobile(formData.client_mobile, formData.country || 'India');
      if (!mobileValidation.valid) {
        newErrors.client_mobile = mobileValidation.message;
      } else if (mobileValidation.cleanedValue && mobileValidation.cleanedValue !== formData.client_mobile) {
        // Note: We can't update state during validation, so cleaned value will be used in payload
      }
    }

    // Validate product selection
    if (!formData.main_product_type || !formData.main_product_type_slug) {
      newErrors.main_product_type = 'Product selection is required';
    }

    // Validate medium
    if (!formData.medium) {
      newErrors.medium = 'Medium is required';
    }

    // Validate source (required if medium is selected)
    if (formData.medium && !formData.source) {
      newErrors.source = 'Source is required';
    }

    // Validate status
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    // Validate enquiry date
    if (!formData.enquiry_date) {
      newErrors.enquiry_date = 'Enquiry date is required';
    }

    // Validate requirement items: Item Name required when a card exists
    requirement.forEach((item, index) => {
      if (!(item.item_name || '').trim()) {
        newErrors[`requirement_${index}_item_name`] = 'Item Name is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Clean mobile number before submission
    let cleanedMobile = formData.client_mobile || '';
    if (cleanedMobile) {
      const mobileValidation = validateMobile(cleanedMobile, formData.country || 'India');
      if (mobileValidation.cleanedValue) {
        cleanedMobile = mobileValidation.cleanedValue;
      }
    }

    // Build requirement payload: ensure item_slug and item_total_price per item
    const requirementPayload = requirement.map((item) => ({
      item_slug: item.item_slug || slugFromItemName(item.item_name),
      item_name: (item.item_name || '').trim(),
      item_unit_price: parseFloat(item.item_unit_price) || 0,
      item_quantity: Math.max(1, parseInt(item.item_quantity, 10) || 1),
      item_total_price: (parseFloat(item.item_unit_price) || 0) * (Math.max(1, parseInt(item.item_quantity, 10) || 1)),
      included_items: item.included_items || [],
      additional_details: (item.additional_details || '').trim(),
    }));

    // Construct payload as per specification (client_id included for EDIT so backend keeps link)
    const payload = {
      ...(formData.client_id ? { client_id: formData.client_id } : {}),
      client_name: formData.client_name.trim(),
      client_email: (formData.client_email || '').trim(),
      client_mobile: cleanedMobile,
      city: (formData.city || '').trim(),
      state: (formData.state || '').trim(),
      country: (formData.country || '').trim(),
      country_code: (formData.country_code || '').trim(),
      enquiry_date: formData.enquiry_date,
      main_product_type: formData.main_product_type,
      main_product_type_slug: formData.main_product_type_slug,
      medium: formData.medium || '',
      source: formData.source || '',
      source_comment: (formData.source_comment || '').trim(),
      comment: (formData.comment || '').trim(),
      current_status: formData.status || '',
      requirement: requirementPayload,
    };

    if (onSubmit) {
      // Edit mode: pass enquiryId so parent calls UPDATE API; Create: no second arg
      if (isEditMode && enquiryId) {
        onSubmit(payload, enquiryId);
      } else {
        onSubmit(payload);
      }
    }
  };

  const getSourceOptions = () => {
    if (!formData.medium) return [];
    return SOURCE_MAPPING[formData.medium] || [];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: '90vh' },
        }
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
          <DialogTitle sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600, p: 0 }}>
            {isEditMode ? 'Edit Enquiry' : 'Add New Enquiry'}
          </DialogTitle>
          <IconButton
            size={isMobile ? 'medium' : 'small'}
            onClick={onClose}
            sx={{
              padding: { xs: '8px', sm: '4px' },
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
          </IconButton>
        </Box>
        <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

        {errors.form && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {errors.form}
          </Typography>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ p: 0, pt: 1, maxHeight: { xs: 'calc(100vh - 200px)', sm: '60vh' }, overflowY: 'auto' }}>
              
              {/* Client Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
                  Client Information
                </Typography>
                
                {!isNewClient ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                    <ClientAutocomplete
                      value={selectedClient}
                      onSelect={handleClientSelect}
                      placeholder="Select Client"
                      required
                      error={!!errors.client_name}
                      helperText={errors.client_name}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleNewClientClick}
                      size="small"
                      sx={{ 
                        whiteSpace: 'nowrap',
                        fontSize: '0.8125rem',
                        mt: 0.5
                      }}
                    >
                      New Client
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="text"
                      onClick={handleBackToClientList}
                      size="small"
                      sx={{ 
                        mb: 1.5,
                        fontSize: '0.8125rem',
                        textTransform: 'none'
                      }}
                    >
                      ← Back to Client List
                    </Button>
                    <TextField
                      label="Client Name"
                      value={formData.client_name}
                      onChange={(e) => handleFieldChange('client_name', e.target.value)}
                      fullWidth
                      size="small"
                      required
                      error={!!errors.client_name}
                      helperText={errors.client_name}
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}

                {/* Client Details Fields */}
                {isNewClient && <Box>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => handleFieldChange('client_email', e.target.value)}
                  fullWidth
                  size="small"
                  required={isNewClient || !selectedClient}
                  disabled={!!selectedClient && !isNewClient}
                  error={!!errors.client_email}
                  helperText={errors.client_email}
                  sx={{ mb: 2 }}
                />
                
                <TextField
                  label="Mobile"
                  value={formData.client_mobile}
                  onChange={(e) => handleFieldChange('client_mobile', e.target.value)}
                  fullWidth
                  size="small"
                  required={isNewClient || !selectedClient}
                  disabled={!!selectedClient && !isNewClient}
                  error={!!errors.client_mobile}
                  helperText={errors.client_mobile}
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ mb: 2 }}>
                  <VirtualizedAutocomplete
                    label="Country"
                    options={countryOptions}
                    value={formData.country}
                    onChange={(v) => handleFieldChange('country', v)}
                    disabled={!!selectedClient && !isNewClient}
                    placeholder="Select country"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <VirtualizedAutocomplete
                    label="State"
                    options={stateOptions}
                    value={formData.state}
                    onChange={(v) => handleFieldChange('state', v)}
                    disabled={!!selectedClient && !isNewClient}
                    placeholder="Select state"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <VirtualizedAutocomplete
                    label="City"
                    options={cityOptions}
                    value={formData.city}
                    onChange={(v) => handleFieldChange('city', v)}
                    disabled={!!selectedClient && !isNewClient}
                    placeholder="Select city"
                  />
                </Box>
                {/* <TextField
                  label="Country Code"
                  value={formData.country_code}
                  onChange={(e) => handleFieldChange('country_code', e.target.value)}
                  fullWidth
                  size="small"
                  disabled={!!selectedClient && !isNewClient}
                  sx={{ mb: 2 }}
                /> */}
                </Box>}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Product Selection - Hierarchical */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
                  Product Selection
                </Typography>
                
                <ProductHierarchyDropdowns
                  selectionPath={productSelectionPath}
                  onSelectionChange={handleProductCategorySelect}
                  error={errors.main_product_type}
                  loading={loading}
                />
              </Box>

              {/* Components / Requirements – just below Product Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
                  Requirements
                </Typography>
                {requirement.map((item, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      position: 'relative',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => removeComponentCard(index)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main', backgroundColor: 'action.hover' },
                      }}
                      aria-label="Delete component"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        flexWrap: 'wrap',
                        gap: 2,
                        pr: 4,
                      }}
                    >
                      <TextField
                        label="Item Name"
                        value={item.item_name || ''}
                        onChange={(e) => updateRequirementItem(index, 'item_name', e.target.value)}
                        fullWidth
                        size="small"
                        required
                        error={!!errors[`requirement_${index}_item_name`]}
                        helperText={errors[`requirement_${index}_item_name`]}
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                      />
                      <TextField
                        label="Unit Price"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={item.item_unit_price === 0 ? '' : item.item_unit_price}
                        onChange={(e) => updateRequirementItem(index, 'item_unit_price', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ minWidth: { xs: '100%', sm: 120 } }}
                      />
                      <TextField
                        label="Quantity"
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                        value={item.item_quantity ?? 1}
                        onChange={(e) => updateRequirementItem(index, 'item_quantity', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ minWidth: { xs: '100%', sm: 100 } }}
                      />
                      <TextField
                        label="Total Price"
                        value={item.item_total_price ?? 0}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ minWidth: { xs: '100%', sm: 120 } }}
                      />
                    </Box>
                    <TextField
                      label="Additional Details"
                      value={item.additional_details || ''}
                      onChange={(e) => updateRequirementItem(index, 'additional_details', e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  </Paper>
                ))}
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addComponentCard}
                  size="small"
                  sx={{ mt: requirement.length > 0 ? 0 : 0 }}
                >
                  Add Requirements
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Enquiry Details */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>
                  Enquiry Details
                </Typography>
                
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Enquiry Date"
                    value={formData.enquiry_date ? dayjs(formData.enquiry_date) : dayjs()}
                    onChange={(newValue) => {
                      const dateValue = newValue ? newValue.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
                      handleFieldChange('enquiry_date', dateValue);
                    }}
                    slotProps={{ 
                      textField: { 
                        size: 'small', 
                        fullWidth: true, 
                        required: true,
                        error: !!errors.enquiry_date,
                        helperText: errors.enquiry_date,
                        sx: { mb: 2 }
                      } 
                    }}
                  />
                </LocalizationProvider>

                <TextField
                  select
                  label="Medium"
                  value={formData.medium}
                  onChange={(e) => handleFieldChange('medium', e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.medium}
                  helperText={errors.medium}
                  sx={{ mb: 2 }}
                >
                  {MEDIUM_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Source"
                  value={formData.source}
                  onChange={(e) => handleFieldChange('source', e.target.value)}
                  fullWidth
                  size="small"
                  required={!!formData.medium}
                  disabled={!formData.medium}
                  error={!!errors.source}
                  helperText={errors.source}
                  sx={{ mb: 2 }}
                >
                  {getSourceOptions().map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Source Comment"
                  value={formData.source_comment}
                  onChange={(e) => handleFieldChange('source_comment', e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />

                <TextField
                  select
                  label="Status"
                  value={formData.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.status}
                  helperText={errors.status}
                  sx={{ mb: 2 }}
                >
                  {ENQUIRY_STATUS.map((status) => (
                    <MenuItem key={status} value={status}>
                      <StatusChip status={status} />
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Additional Comment"
                  value={formData.comment || ''}
                  onChange={(e) => handleFieldChange('comment', e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  placeholder="Optional comment for this enquiry"
                  sx={{ mb: 2 }}
                />

                {/* Employee field removed - backend automatically uses logged-in user's employee_id */}
              </Box>
            </DialogContent>

            <DialogActions sx={{ 
              px: 0, 
              pb: { xs: 1, sm: 0 }, 
              pt: { xs: 2, sm: 2 }, 
              gap: { xs: 1, sm: 1.5 },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              '& > *': {
                width: { xs: '100%', sm: 'auto' },
                margin: { xs: '0 !important', sm: 'initial' },
              }
            }}>
              <Button
                onClick={onClose}
                variant="outlined"
                size={isMobile ? 'medium' : 'small'}
                sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                size={isMobile ? 'medium' : 'small'}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  },
                }}
              >
                {isEditMode ? 'Update Enquiry' : 'Create Enquiry'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Box>
    </Dialog>
  );
};

/**
 * Product Hierarchy Dropdowns Component
 * Renders dynamic hierarchical dropdowns for product selection
 */
const ProductHierarchyDropdowns = ({ selectionPath, onSelectionChange, error, loading }) => {
  // Build dropdown levels based on selection path
  const buildDropdownLevels = () => {
    const levels = [];
    
    // Level 0: Always show top-level categories
    const topCategories = getTopLevelCategories();
    const level0Value = selectionPath.length > 0 ? selectionPath[0] : '';
    
    levels.push({
      level: 0,
      options: topCategories,
      placeholder: 'Select Category',
      value: level0Value
    });

    // Build subsequent levels for each item in the path
    // navigateHierarchy(path) returns children of items in the path
    // So for level i+1: options = navigateHierarchy(selectionPath.slice(0, i+1))
    for (let i = 0; i < selectionPath.length; i++) {
      // Path to get children for this level: all selections up to current level
      const pathToCurrentLevel = selectionPath.slice(0, i + 1);
      const currentOptions = navigateHierarchy(pathToCurrentLevel.slice(0, i + 1).slice(0, -1));
      
      if (currentOptions.length === 0) break;

      const currentValue = selectionPath[i];
      const selectedItem = currentOptions.find(opt => opt.id === currentValue);
      
      if (!selectedItem) break;

      // Determine placeholder based on item type
      let placeholder = 'Select Product';
      if (selectedItem.type === 'sub_category' || selectedItem.type === 'category') {
        placeholder = 'Select Sub Category';
      }

      levels.push({
        level: i + 1,
        options: currentOptions,
        placeholder: placeholder,
        value: currentValue,
        isLeaf: selectedItem.isLeaf || false,
        hasChildren: selectedItem.hasChildren || false
      });

      // If this is a leaf, stop adding more levels
      if (selectedItem.isLeaf) {
        break;
      }
    }

    // Add next level dropdown if last selection has children
    if (selectionPath.length > 0) {
      // Get options for next level (children of current path)
      const nextLevelOptions = navigateHierarchy(selectionPath);
      
      // Find the last selected item to check if it has children
      const parentPath = selectionPath.slice(0, selectionPath.length - 1);
      const parentOptions = navigateHierarchy(parentPath);
      const lastSelected = parentOptions.find(opt => opt.id === selectionPath[selectionPath.length - 1]);
      
      // If last selection has children and is not a leaf, show next level
      if (lastSelected && lastSelected.hasChildren && !lastSelected.isLeaf && nextLevelOptions.length > 0) {
        levels.push({
          level: selectionPath.length + 1,
          options: nextLevelOptions,
          placeholder: 'Select Product',
          value: '',
          isLeaf: false,
          hasChildren: false
        });
      }
    }

    return levels;
  };

  const dropdownLevels = buildDropdownLevels();

  if (dropdownLevels.length === 0) {
    return (
      <TextField
        label="Select Category"
        disabled
        fullWidth
        size="small"
        helperText="No categories available"
        sx={{ mb: 2 }}
      />
    );
  }

  return (
    <Box>
      {dropdownLevels.map((levelData, index) => {
        const isLastLevel = index === dropdownLevels.length - 1;
        const selectedItem = levelData.options.find(opt => opt.id === levelData.value);
        const isLeafSelected = selectedItem?.isLeaf || levelData.isLeaf;

        return (
          <TextField
            key={`level-${levelData.level}-${index}`}
            select
            label={levelData.placeholder}
            value={levelData.value || ''}
            onChange={(e) => onSelectionChange(levelData.level, e.target.value)}
            fullWidth
            size="small"
            required={isLastLevel}
            error={!!error && isLastLevel}
            helperText={
              error && isLastLevel ? error : 
              isLeafSelected ? '✓ Product selected' : 
              levelData.value ? 'Continue selecting...' : 
              'Select an option to continue'
            }
            disabled={loading}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">
              -- {levelData.placeholder} --
            </MenuItem>
            {levelData.options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
                {option.isLeaf ? ' ✓' : ''}
              </MenuItem>
            ))}
          </TextField>
        );
      })}
    </Box>
  );
};

export default AddEnquiryDialog;

